import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PropertyReformDetails from "../components/PropertyReformDetails";
import AdvancedInvestmentIndicators from "../components/AdvancedInvestmentIndicators";
import LoginRequiredModal from "../components/LoginRequiredModal";
import { FaStar, FaRegStar } from "react-icons/fa";
import AdvancedInvestmentCharts from "../components/AdvancedInvestmentCharts";
import AlquilerVacacionalDetails from "../components/AlquilerVacacionalDetails";
import { authFetch } from "../utils/authFetch";
import LoginButton from "../components/LoginButton";
import CollapsiblePanel from "../components/CollapsiblePanel";
import DownloadPDFButton from "../components/DownloadPDFButton";
import BotonAnalizarIA from "../components/BotonAnalizarIA";
import RequireRole from "../components/RequireRol";
import ImageCarousel from "../components/ImageCarousel";
import Spinner from "../components/Spinner";
import InvestmentLoader from '../components/InvestmentLoader';
import ConfirmationModal from "../components/ConfirmationModal";

const PropertyDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [propiedad, setPropiedad] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modoComparacion, setModoComparacion] = useState(false);
    const [starred, setStarred] = useState(false);
    const [usuarioLogado, setUsuarioLogado] = useState(false);
    const [indicadores, setIndicadores] = useState([]);
    const [indicadoresAvanzados, setIndicadoresAvanzados] = useState([]);
    const [indicadoresAvanzadosIA, setIndicadoresAvanzadosIA] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [costesVacacional, setCostesVacacional] = useState(null);
    const [indicadoresVacacional, setIndicadoresVacacional] = useState(null);
    const [loadingVacacional, setLoadingVacacional] = useState(false);
    const [descripcionExpandida, setDescripcionExpandida] = useState(false);
    const [descripcionCorta, setDescripcionCorta] = useState(false);
    const [resumenIA, setResumenIA] = useState(null);
    const [modoSoloIA, setModoSoloIA] = useState(false);
    const [imagenes, setImagenes] = useState([]);
    const [loadingImagenes, setLoadingImagenes] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [detailsItem, setDetailsItem] = useState(null);

    const toggleDescripcion = () => setDescripcionExpandida(!descripcionExpandida);

    const onUnauthorized = () => {
        setUsuarioLogado(false);
        setShowModal(true);
    };

    useEffect(() => {
        setLoading(true);
        setError(null);
        
        fetch(`${window.REACT_APP_API_ENDPOINT}/propiedades/${id}`, {
            credentials: 'include'
        })
            .then((res) => {
                if (!res.ok) {
                    const error = new Error(res.statusText);
                    error.status = res.status;
                    throw error;
                }
                return res.json();
            })
            .then((data) => {
                if (!data) {
                    setError('no_encontrada');
                    setLoading(false);
                    return;
                }
                setPropiedad(data);
                setDescripcionCorta(data.descripcion?.slice(0, 250));
                setError(null);
                setLoading(false);
            })
            .catch((err) => {
                console.error('Error fetching property:', err);
                setPropiedad(null);
                if (err.status === 403 || err.status === 404) {
                    setError('no_encontrada');
                } else {
                    setError('error_carga');
                }
                setLoading(false);
            });
    }, [id]);

    useEffect(() => {
        fetch(`${window.REACT_APP_API_ENDPOINT}/api/reformas-ia/propiedad/${id}/obtener-resumen-ia`, {
            credentials: "include"
        })
            .then(res => res.json())
            .then(data => setResumenIA(data))
            .catch(err => console.error("Error al obtener resumen IA:", err));
    }, [id]);

    useEffect(() => {
        fetch(`${window.REACT_APP_API_ENDPOINT}/starred/${id}`, {
            credentials: "include"
        })
            .then((res) => {
                if (res.status === 401) {
                    setUsuarioLogado(false);
                    return { starred: false };
                }
                setUsuarioLogado(true);
                return res.json();
            })
            .then((data) => setStarred(data.starred))
            .catch((err) => {
                console.error("Error verificando favorito:", err);
                setUsuarioLogado(false);
            });
    }, [id]);

    useEffect(() => {
        const cargarImagenes = async () => {
            setLoadingImagenes(true);
            try {
                const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/propiedades/${id}/imagenes`, {
                    credentials: 'include'
                });
                const data = await res.json();

                // Filter out any null or undefined image URLs
                const imagenesValidas = data.filter(img => img.url_imagen);

                // Only include the main image if it exists and isn't already in the list
                const yaIncluida = imagenesValidas.some(img => img.url_imagen === propiedad.imagen_url);
                const imagenesFinales = (propiedad.imagen_url && !yaIncluida)
                    ? [{ url_imagen: propiedad.imagen_url }, ...imagenesValidas]
                    : imagenesValidas;

                setImagenes(imagenesFinales);
            } catch (err) {
                console.error("Error cargando imágenes:", err);
                // If there's an error loading images, try to use the main image if it exists
                if (propiedad.imagen_url) {
                    setImagenes([{ url_imagen: propiedad.imagen_url }]);
                } else {
                    setImagenes([]);
                }
            } finally {
                setLoadingImagenes(false);
            }
        };

        if (propiedad) cargarImagenes();
    }, [id, propiedad]);


    const cargarReformasConIA = async () => {
        try {
            const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/api/reformas-ia/propiedad/${id}/obtener-reformas-sugerencias-ia`, {
                credentials: "include",
            });
            const data = await res.json();
            if (data && propiedad) {
                setPropiedad(prev => ({
                    ...prev,
                    reformas: data
                }));
            }
        } catch (err) {
            console.error("Error cargando reformas con sugerencias IA:", err);
        }
    };

    const toggleStarred = async () => {
        const url = `${window.REACT_APP_API_ENDPOINT}/starred/${id}`;
        const method = starred ? "DELETE" : "POST";
        const res = await fetch(url, { method, credentials: "include" });
        if (res.ok) {
            setStarred(!starred);
        }
    };

    const calcularIndicadoresAvanzados = async () => {
        if (!usuarioLogado) return onUnauthorized();
        const data = await authFetch(`${window.REACT_APP_API_ENDPOINT}/indicadores/calcular-indicadores-avanzados/${id}`, {}, onUnauthorized);
        if (data) {
            setIndicadoresAvanzados(data);
            const vacacional = data["Alquiler Vacacional"];
            if (vacacional?.alquiler?.length > 0) setIndicadoresVacacional(vacacional.alquiler);
        }
    };

    const calcularIndicadoresAvanzadosIA = async () => {
        if (!usuarioLogado) return onUnauthorized();
        const data = await authFetch(`${window.REACT_APP_API_ENDPOINT}/indicadores/calcular-indicadores-avanzados/${id}?soloIA=true`, {}, onUnauthorized);
        if (data) {
            setIndicadoresAvanzadosIA(data);
        }
    };


    const calcularCostesVacacional = async () => {
        if (!usuarioLogado) return onUnauthorized();

        let data = await authFetch(`${window.REACT_APP_API_ENDPOINT}/alquiler-vacacional/${id}`, {}, onUnauthorized);

        if (data?.costes?.costes?.detalles && Object.values(data.costes.costes.detalles).every(arr => Array.isArray(arr) && arr.length === 0)) {
            await authFetch(`${window.REACT_APP_API_ENDPOINT}/alquiler-vacacional/${id}`, { method: "POST" }, onUnauthorized);
            data = await authFetch(`${window.REACT_APP_API_ENDPOINT}/alquiler-vacacional/${id}`, {}, onUnauthorized);
        }

        if (data) setCostesVacacional(data);
    };

    const cargarCostesVacacional = async () => {
        console.log('🔍 PropertyDetails - cargarCostesVacacional called');
        if (!usuarioLogado) {
            console.log('❌ PropertyDetails - User not logged in');
            return onUnauthorized();
        }
        try {
            console.log('🔍 PropertyDetails - Making API call to alquiler-vacacional');
            const data = await authFetch(`${window.REACT_APP_API_ENDPOINT}/alquiler-vacacional/${id}`, {}, onUnauthorized);
            console.log('🔍 PropertyDetails - API response:', data);
            if (data) {
                console.log('🔍 PropertyDetails - Setting costesVacacional state');
                setCostesVacacional(data);
            } else {
                console.log('❌ PropertyDetails - No data received from API');
            }
        } catch (error) {
            console.error("❌ PropertyDetails - Error loading vacation rental costs:", error);
        }
    };

    const calcularIndicadoresVacacional = async () => {
        if (!usuarioLogado) return onUnauthorized();
        setLoadingVacacional(true);
        try {
            // First calculate vacation rental costs
            await authFetch(`${window.REACT_APP_API_ENDPOINT}/alquiler-vacacional/${id}`, { method: "GET" }, onUnauthorized);
            
            // Then refresh the investment indicators to include vacation rental scenarios
            await calcularIndicadoresAvanzados();
        } catch (error) {
            console.error("Error calculating vacation rental indicators:", error);
        } finally {
            setLoadingVacacional(false);
        }
    };

    // Add this handler for details
    const handleShowDetails = (item) => {
        setDetailsItem(item);
        setShowDetailsModal(true);
    };

    if (loading) {
        return (
            <div className="container mx-auto p-6 bg-gray-100 min-h-screen">
                <button onClick={() => navigate(-1)} className="mb-4 text-blue-600 hover:underline">← Volver</button>
                <div className="bg-white shadow-md rounded-lg p-6 flex items-center justify-center min-h-96">
                    <Spinner 
                        size="lg" 
                        text="Cargando propiedad..." 
                        color="blue"
                    />
                </div>
            </div>
        );
    }

    if (error || !propiedad) {
        return (
            <div className="container mx-auto p-6 bg-gray-100 min-h-screen">
                <button onClick={() => navigate(-1)} className="mb-4 text-blue-600 hover:underline">← Volver</button>
                <div className="bg-white shadow-md rounded-lg p-6 text-center">
                    <h2 className="text-2xl font-bold text-red-600 mb-4">
                        {error === 'no_encontrada' ? 'No se ha encontrado la propiedad' : 'Error al cargar la propiedad'}
                    </h2>
                    <p className="text-gray-600 mb-4">
                        {error === 'no_encontrada' 
                            ? 'Lo sentimos, no podemos mostrar esta propiedad en este momento. Puede que no esté disponible o que no tengas los permisos necesarios para verla.'
                            : 'Ha ocurrido un error inesperado. Por favor, intenta de nuevo más tarde o contacta con soporte si el problema persiste.'}
                    </p>
                    <button
                        onClick={() => navigate('/')}
                        className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
                    >
                        Volver a la página principal
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-6 bg-gray-100 min-h-screen">
            <button onClick={() => navigate(-1)} className="mb-4 text-blue-600 hover:underline">← Volver</button>

            <div className="bg-white shadow-md rounded-lg p-6 mb-6 relative">
                {usuarioLogado && (
                    <button
                        className="absolute top-4 right-4 text-yellow-400 text-2xl hover:text-yellow-500 transition"
                        onClick={toggleStarred}
                        title={starred ? "Quitar de favoritos" : "Añadir a favoritos"}
                    >
                        {starred ? <FaStar /> : <FaRegStar />}
                    </button>
                )}
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                    {propiedad.habitaciones > 0 ? `${propiedad.habitaciones} hab.` : "Estudio"} – {propiedad.banos} baño{propiedad.banos > 1 ? "s" : ""}, {propiedad.superficie} m²
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                    {propiedad.distrito ? `${propiedad.distrito}, ${propiedad.municipio}` : propiedad.municipio}
                </p>

                <p className="text-xl font-bold text-blue-600 mt-2">
                    {parseFloat(propiedad.precio).toLocaleString(undefined, { minimumFractionDigits: 2 })} €
                </p>

                {propiedad.superficie && (
                    <p className="text-gray-700 mt-1">
                        Precio por m²: {(propiedad.precio / propiedad.superficie).toFixed(2)} €
                    </p>
                )}

                <div className="mb-6">
                    {loadingImagenes ? (
                        <div className="flex items-center justify-center h-64 bg-gray-100 rounded-lg">
                            <Spinner 
                                size="md" 
                                text="Cargando imágenes..." 
                                color="blue"
                            />
                        </div>
                    ) : (
                        <ImageCarousel images={imagenes} />
                    )}
                </div>

                {usuarioLogado && (
                    <div className="mt-6">
                        <DownloadPDFButton propiedadId={propiedad.id} />
                    </div>)}

                {usuarioLogado && (
                    <RequireRole allowedRoles={['admin', 'gestor_inmobiliario']} showError={false}>
                        <div className="mt-6 bg-blue-100 p-4 rounded-lg shadow-md border border-blue-200">
                            <div className="flex justify-between items-center">
                                <div>
                                    <BotonAnalizarIA
                                        propiedadId={propiedad.id}
                                        resumenIA={resumenIA}
                                        setResumenIA={setResumenIA}
                                        imageCount={imagenes.length}
                                    />
                                </div>

                                {resumenIA && !resumenIA.error && (
                                    <div className="flex flex-col items-center space-y-1">
    <span className="text-xs font-semibold text-gray-700">
      {modoSoloIA ? "IA ON" : "IA OFF"}
    </span>
                                        <label className="inline-flex relative items-center cursor-pointer scale-110">
                                            <input
                                                type="checkbox"
                                                className="sr-only peer"
                                                checked={modoSoloIA}
                                                onChange={(e) => {
                                                    setModoSoloIA(e.target.checked);
                                                    if (e.target.checked) cargarReformasConIA();
                                                }}
                                            />
                                            <div className="w-14 h-8 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-400 rounded-full peer dark:bg-gray-700 peer-checked:bg-blue-600 relative">
        <span className="absolute left-1 top-1 w-6 h-6 bg-white rounded-full transition-transform duration-300 ease-in-out peer-checked:translate-x-6 flex items-center justify-center text-yellow-400 text-lg">
          {modoSoloIA ? "💡" : ""}
        </span>
                                            </div>
                                        </label>
                                    </div>
                                )}

                            </div>

                            {/* Mensaje de mínimo de imágenes */}
                            {imagenes.length < 4 && (
                                <p className="text-sm text-yellow-600 mt-3">
                                    ⚠️ Esta propiedad no tiene suficientes imágenes para ser analizada (mínimo 4 requeridas).
                                </p>
                            )}

                            {/* Valoración IA */}
                            {resumenIA && !resumenIA.error && (
                                <div className="mt-4 border border-blue-300 bg-white rounded p-4 text-sm text-gray-800">
                                    <div className="flex items-center space-x-3 mb-2">
                                        <span className="font-semibold">Estado:</span>
                                        <span>{resumenIA.estadoGeneral}/10</span>

                                        {/* Contenedor de barra + tooltip */}
                                        <div className="relative group">
                                            <div className="w-32 h-3 bg-gray-200 rounded overflow-hidden">
                                                <div
                                                    className={`h-3 rounded ${
                                                        parseFloat(resumenIA.estadoGeneral) >= 7
                                                            ? "bg-green-500"
                                                            : parseFloat(resumenIA.estadoGeneral) >= 4
                                                                ? "bg-yellow-400"
                                                                : "bg-red-500"
                                                    }`}
                                                    style={{
                                                        width: `${(parseFloat(resumenIA.estadoGeneral) / 10) * 100}%`,
                                                    }}
                                                />
                                            </div>

                                            {/* Tooltip */}
                                            <div className="absolute -top-14 left-1/2 transform -translate-x-1/2 scale-0 group-hover:scale-100 transition-all duration-200 bg-gray-900 text-white text-xs px-3 py-2 rounded shadow-lg z-10 w-64 text-center">
                                                Valoración IA del estado general:<br />
                                                <span className="block mt-1">
      <span className="text-red-400 font-semibold">0–3:</span> Malo ·
      <span className="text-yellow-400 font-semibold"> 4–6:</span> Mejorable ·
      <span className="text-green-400 font-semibold"> 7–10:</span> Bueno
    </span>
                                            </div>
                                        </div>

                                    </div>
                                    <p><strong>Valoración general:</strong> {resumenIA.valoracion_estado}</p>
                                    <p><strong>Dificultad y tiempo:</strong> {resumenIA.dificultad_tiempo}</p>
                                    <p><strong>Potencial de mejora:</strong> {resumenIA.potencial_mejora}</p>
                                </div>
                            )}
                        </div>
                    </RequireRole>

                )}

                {propiedad.descripcion && (
                    <div className="mt-4">
                        <h2 className="text-lg font-semibold mb-1 text-gray-800">Descripción</h2>
                        <p className="text-gray-700 whitespace-pre-line">
                            {descripcionExpandida ? propiedad.descripcion : `${descripcionCorta}...`}
                        </p>
                        <button
                            onClick={toggleDescripcion}
                            className="text-sm text-blue-600 mt-1 hover:underline focus:outline-none"
                        >
                            {descripcionExpandida ? "Mostrar menos" : "Leer más"}
                        </button>
                    </div>
                )}
            </div>
            <CollapsiblePanel
                title="📊 Indicadores de inversión avanzados"
                previewContent="ROI, tiempo de retorno, precio estimado..."
                renderContent={() => (
                    <AdvancedInvestmentIndicators
                        indicadores={modoSoloIA ? indicadoresAvanzadosIA : indicadoresAvanzados}
                        indicadoresOriginales={!modoSoloIA ? null : indicadoresAvanzados}
                        vacacional={indicadoresVacacional}
                        loadingVacacional={loadingVacacional}
                        onCalcularVacacional={calcularIndicadoresVacacional}
                        onShowDetails={handleShowDetails}
                        costesVacacional={costesVacacional}
                        onCargarCostesVacacional={cargarCostesVacacional}
                    />
                )}
                onExpand={() => setShowModal(true)}
                onFetch={async () => {
                    await calcularIndicadoresAvanzados();
                    await calcularIndicadoresAvanzadosIA();
                }}
                requireAuth={true}
                isUserLogged={usuarioLogado}
                loaderComponent={InvestmentLoader}
            >
            </CollapsiblePanel>

            <CollapsiblePanel
                title="📊️ Gráficas de índices de inversión"
                previewContent="Comparativa de rentabilidades, ROI..."
                renderContent={() => (
                    <AdvancedInvestmentCharts
                        key={`indicadores-${modoSoloIA ? "ia" : "base"}-${(modoSoloIA ? indicadoresAvanzadosIA : indicadoresAvanzados)?.venta?.length || 0}`}
                        indicadores={modoSoloIA ? indicadoresAvanzadosIA : indicadoresAvanzados}
                        indicadoresVacacional={modoSoloIA ? null : indicadoresVacacional}
                    />
                )}
                onExpand={() => setShowModal(true)}
                onFetch={calcularIndicadoresAvanzados}
                requireAuth={true}
                isUserLogged={usuarioLogado}
                loaderComponent={InvestmentLoader}
            >
            </CollapsiblePanel>

            <CollapsiblePanel
                title="💼 Análisis gastos alquiler vacacional"
                previewContent="Mobiliario, ajuar, licencias, electrodomésticos..."
                renderContent={() => (
                    <AlquilerVacacionalDetails costes={costesVacacional?.costes?.costes} />
                )}
                onExpand={() => setShowModal(true)}
                onFetch={calcularCostesVacacional}
                requireAuth={true}
                isUserLogged={usuarioLogado}
                loaderComponent={InvestmentLoader}
            />

            {propiedad.datos_catastrales && (
                <div className="bg-white shadow-md rounded-lg p-6 mb-6">
                    <h2 className="text-lg font-semibold mb-3 text-gray-800">Datos Catastrales</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-700">
                        <div>
                            <span className="font-medium">Referencia catastral: </span>
                            <span className="font-mono">{propiedad.datos_catastrales.referencia_catastral}</span>
                            <button
                                onClick={() => navigator.clipboard.writeText(propiedad.datos_catastrales.referencia_catastral)}
                                className="ml-2 text-xs text-blue-600 hover:underline"
                                title="Copiar referencia catastral"
                            >
                                Copiar
                            </button>
                            {propiedad.datos_catastrales.codigo_del && propiedad.datos_catastrales.codigo_mun ? (
                                <a
                                    href={`https://www1.sedecatastro.gob.es/CYCBienInmueble/OVCConCiud.aspx?del=${propiedad.datos_catastrales.codigo_del}&mun=${propiedad.datos_catastrales.codigo_mun}&RefC=${propiedad.datos_catastrales.referencia_catastral}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ml-2 text-xs text-blue-600 hover:underline"
                                >
                                    Ver en SEDECatastro ↗
                                </a>
                            ) : (
                                <a
                                    href="https://www1.sedecatastro.gob.es/CYCBienInmueble/OVCBusqueda.aspx"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ml-2 text-xs text-gray-500 hover:underline"
                                >
                                    Buscar en SEDECatastro ↗
                                </a>
                            )}
                        </div>
                        {propiedad.datos_catastrales.superficie_catastral && (
                            <div>
                                <span className="font-medium">Superficie oficial: </span>
                                {propiedad.datos_catastrales.superficie_catastral} m²
                                {propiedad.superficie && Math.abs(propiedad.datos_catastrales.superficie_catastral - propiedad.superficie) / propiedad.superficie > 0.05 && (
                                    <span className="ml-2 text-yellow-600 text-xs font-semibold">
                                        ⚠️ Difiere de los {propiedad.superficie} m² indicados
                                    </span>
                                )}
                            </div>
                        )}
                        {propiedad.datos_catastrales.anno_construccion && (
                            <div>
                                <span className="font-medium">Año de construcción: </span>
                                {propiedad.datos_catastrales.anno_construccion}
                            </div>
                        )}
                        {propiedad.datos_catastrales.codigo_postal && (
                            <div>
                                <span className="font-medium">Código postal: </span>
                                {propiedad.datos_catastrales.codigo_postal}
                            </div>
                        )}
                        {propiedad.datos_catastrales.uso_principal && (
                            <div>
                                <span className="font-medium">Uso principal: </span>
                                {propiedad.datos_catastrales.uso_principal}
                            </div>
                        )}
                    </div>
                    <p className="text-xs text-gray-400 mt-3">Fuente: Sede Electrónica del Catastro (Hacienda)</p>
                </div>
            )}

            {propiedad.reformas && (
                <PropertyReformDetails
                    propiedad={propiedad}
                    indicadores={indicadores || []}
                    modoComparacion={modoComparacion}
                    setModoComparacion={setModoComparacion}
                    soloIA={modoSoloIA}
                    setSoloIA={(activo) => {
                        setModoSoloIA(activo);
                        if (activo) cargarReformasConIA();
                    }}
                    existenReformasIA={resumenIA !== null && !resumenIA?.error}
                />
            )}

            <LoginRequiredModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                message="Para acceder a esta funcionalidad necesitas haber iniciado sesión."
            >
                <div className="mt-4">
                    <LoginButton />
                </div>
            </LoginRequiredModal>

            {resumenIA && !resumenIA.error && (
                <div className="fixed bottom-6 right-6 z-50 bg-white shadow-lg rounded-full px-4 py-2 flex items-center space-x-2 border border-blue-300">
    <span className={`text-sm font-semibold ${modoSoloIA ? "text-blue-700" : "text-gray-500"}`}>
      {modoSoloIA ? "IA ON" : "IA OFF"}
    </span>
                    <label className="inline-flex relative items-center cursor-pointer scale-110">
                        <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={modoSoloIA}
                            onChange={(e) => {
                                setModoSoloIA(e.target.checked);
                                if (e.target.checked) cargarReformasConIA();
                            }}
                        />
                        <div className="w-12 h-7 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 relative">
                            <span className="absolute left-1 top-1 w-5 h-5 bg-white rounded-full transition-transform duration-300 ease-in-out peer-checked:translate-x-5"></span>
                        </div>
                    </label>
                </div>
            )}

            {/* Details Modal */}
            <ConfirmationModal
                isOpen={showDetailsModal}
                onClose={() => setShowDetailsModal(false)}
                onConfirm={() => setShowDetailsModal(false)}
                title="Detalles del escenario"
                message={<pre className="text-xs whitespace-pre-wrap">{JSON.stringify(detailsItem, null, 2)}</pre>}
                confirmText="Cerrar"
                cancelText=""
            />

        </div>
    );
};

export default PropertyDetail;
